/**
 * @sazeo/sdk — Payroll hooks
 *
 * Hooks that call the payroll Edge Function handlers. All requests carry the
 * caller's Supabase JWT so RLS enforces tenant isolation automatically.
 *
 * Paired spec: specs/CODEX_PAYROLL_ADDON.md
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export type PayFrequency = 'monthly' | 'biweekly' | 'weekly';
export type RunStatus = 'draft' | 'processing' | 'approved' | 'paid' | 'cancelled';

export interface SalaryRecord {
  id: string;
  tenant_id: string;
  user_id: string;
  gross_amount: number;
  currency: string;
  effective_from: string;
  effective_to: string | null;
  pay_frequency: PayFrequency;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface PayrollRun {
  id: string;
  tenant_id: string;
  period_start: string;
  period_end: string;
  run_no: string;
  status: RunStatus;
  notes: string | null;
  approved_by: string | null;
  approved_at: string | null;
  paid_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  pay_slips?: PaySlip[];
}

export interface PaySlip {
  id: string;
  tenant_id: string;
  payroll_run_id: string;
  user_id: string;
  gross_amount: number;
  income_tax: number;
  pension_contrib: number;
  other_deductions: number;
  net_amount: number;
  currency: string;
  leave_days_used: number;
  expense_amount: number;
  notes: string | null;
  created_at: string;
}

export interface SalaryRecordFilters {
  userId?: string;
}

export interface PayrollRunFilters {
  status?: RunStatus;
  year?: number;
}

export interface UpsertSalaryRecordInput {
  gross_amount: number;
  currency?: string;
  effective_from: string;
  effective_to?: string | null;
  pay_frequency?: PayFrequency;
  notes?: string | null;
}

export interface CreatePayrollRunInput {
  periodStart: string;
  periodEnd: string;
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// Internal fetch helpers (same pattern as expenses.ts)
// ---------------------------------------------------------------------------

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(supabase: unknown): SupabaseAuthLike {
  if (supabase) return supabase as SupabaseAuthLike;
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return { auth: { getSession: async () => ({ data: { session: null } }) } };
  }
}

async function getAuthHeaders(supabase: unknown): Promise<Record<string, string>> {
  const client = resolveClient(supabase);
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(supabase: unknown, path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useSalaryRecords(
  supabase: unknown,
  tenantId: string,
  filters?: SalaryRecordFilters,
) {
  const params = new URLSearchParams({ tenant_id: tenantId });
  if (filters?.userId) params.set('user_id', filters.userId);
  const keyParts: readonly unknown[] = [tenantId, filters?.userId ?? null];

  return useAsyncQuery<SalaryRecord[]>(
    () => apiFetch<SalaryRecord[]>(supabase, `addons/payroll/salary?${params.toString()}`),
    [],
    keyParts,
    Boolean(tenantId),
  );
}

export function useMySalary(supabase: unknown, tenantId: string, userId: string) {
  return useAsyncQuery<SalaryRecord | null>(
    async () => {
      const records = await apiFetch<SalaryRecord[]>(
        supabase,
        `addons/payroll/salary?tenant_id=${tenantId}&user_id=${userId}&active=true`,
      );
      // Active record has effective_to = null; return the most recent one.
      return records.find((r) => r.effective_to === null) ?? records[0] ?? null;
    },
    null,
    [tenantId, userId],
    Boolean(tenantId) && Boolean(userId),
  );
}

export function usePayrollRuns(supabase: unknown, tenantId: string, filters?: PayrollRunFilters) {
  const params = new URLSearchParams({ tenant_id: tenantId });
  if (filters?.status) params.set('status', filters.status);
  if (filters?.year) params.set('year', String(filters.year));
  const keyParts: readonly unknown[] = [tenantId, filters?.status ?? null, filters?.year ?? null];

  return useAsyncQuery<PayrollRun[]>(
    () => apiFetch<PayrollRun[]>(supabase, `addons/payroll/runs?${params.toString()}`),
    [],
    keyParts,
    Boolean(tenantId),
  );
}

export function usePayrollRun(supabase: unknown, tenantId: string, runId: string) {
  return useAsyncQuery<PayrollRun | null>(
    () => apiFetch<PayrollRun>(supabase, `addons/payroll/runs/${runId}?tenant_id=${tenantId}`),
    null,
    [tenantId, runId],
    Boolean(tenantId) && Boolean(runId),
  );
}

export function useMyPaySlips(supabase: unknown, tenantId: string, userId: string) {
  return useAsyncQuery<PaySlip[]>(
    () =>
      apiFetch<PaySlip[]>(supabase, `addons/payroll/slips?tenant_id=${tenantId}&user_id=${userId}`),
    [],
    [tenantId, userId],
    Boolean(tenantId) && Boolean(userId),
  );
}

export function useCreatePayrollRun(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string } & CreatePayrollRunInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<PayrollRun>(supabase, 'addons/payroll/runs', {
          method: 'POST',
          body: JSON.stringify({
            tenant_id: input.tenantId,
            period_start: input.periodStart,
            period_end: input.periodEnd,
            notes: input.notes ?? null,
          }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useApprovePayrollRun(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; runId: string }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<PayrollRun>(supabase, `addons/payroll/runs/${input.runId}/approve`, {
          method: 'POST',
          body: JSON.stringify({ tenant_id: input.tenantId }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

export function useMarkPayrollPaid(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; runId: string }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<PayrollRun>(
          supabase,
          `addons/payroll/runs/${input.runId}/mark-paid`,
          {
            method: 'POST',
            body: JSON.stringify({ tenant_id: input.tenantId }),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}

// ---------------------------------------------------------------------------
// Payslip PDF download helpers
// ---------------------------------------------------------------------------

export async function downloadPayslipPdf(
  supabase: unknown,
  tenantId: string,
  payslipId: string,
): Promise<Blob> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(
    `/functions/v1/addons/payroll/payslips/${payslipId}/pdf?tenant_id=${tenantId}`,
    { headers },
  );
  if (!res.ok) throw new Error('Download failed');
  return res.blob();
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function useUpsertSalaryRecord(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; userId: string } & UpsertSalaryRecordInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<SalaryRecord>(supabase, 'addons/payroll/salary', {
          method: 'PUT',
          body: JSON.stringify({
            tenant_id: input.tenantId,
            user_id: input.userId,
            gross_amount: input.gross_amount,
            currency: input.currency ?? 'GEL',
            effective_from: input.effective_from,
            effective_to: input.effective_to ?? null,
            pay_frequency: input.pay_frequency ?? 'monthly',
            notes: input.notes ?? null,
          }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { mutate, isPending, error };
}
